import type * as React from 'react';
import { getShareableCalendars } from 'owa-calendar-surface-calendars-store';
import { ControlIcons } from 'owa-control-icons';
import { FilterableMenuItem, FilterableMenuItemArgs } from 'owa-filterable-menu';
import { getCalendarFolderId, Calendar } from 'owa-calendar-cache';
import { lazyShowCalendarSharingPermissionsDialog } from 'owa-calendar-sharing';
import { getCalendarColorScheme } from 'owa-calendar-color-resolver';
import { ContextualMenuItemType, IContextualMenuItem } from '@fluentui/react/lib/ContextualMenu';
import { DefaultPalette } from '@fluentui/style-utilities/lib/index';
import { getOWAConnectedAccounts } from 'owa-accounts-store';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import { ObservableMap } from 'mobx';

function getCombinedCalendarMenuItems(
    sharedCalendarMenuItemsMap: ObservableMap<string, FilterableMenuItem[]>[]
): IContextualMenuItem[] {
    const shareMenuItem: React.CSSProperties = {
        color: DefaultPalette.neutralTertiary,
        fontWeight: 400,
    };

    let menuItems = [];
    sharedCalendarMenuItemsMap.forEach(sharedCalendarMenuItem => {
        if (sharedCalendarMenuItem) {
            sharedCalendarMenuItem.forEach((value, key) => {
                menuItems.push({
                    key: key,
                    itemType: ContextualMenuItemType.Header,
                    text: key,
                    style: shareMenuItem,
                    iconProps: {
                        style: {
                            display: 'none',
                        },
                    },
                });

                menuItems.push(...value);
            });
        }
    });

    return menuItems;
}

function getBulletedCalendarMenuItem(
    calendar: Calendar,
    menuItemClassName: string
): FilterableMenuItem {
    let bulletColor = calendar.id ? getCalendarColorScheme(calendar.id).calPrimary : null;
    let calendarId = calendar.id?.id;
    let menuItem = new FilterableMenuItem({
        [FilterableMenuItemArgs.name]: calendar.displayName,
        [FilterableMenuItemArgs.onClick]: () => {
            lazyShowCalendarSharingPermissionsDialog.importAndExecute(
                getCalendarFolderId(calendar),
                calendarId,
                calendar.id.mailboxInfo,
                bulletColor,
                calendar.displayName
            );
        },
        [FilterableMenuItemArgs.key]: calendarId,
        [FilterableMenuItemArgs.title]: calendar.displayName,
        [FilterableMenuItemArgs.className]: menuItemClassName,
    });
    menuItem.iconProps = {
        iconName: ControlIcons.CircleFill,
        style: {
            color: bulletColor,
            fontSize: '8px',
            margin: '0 2px 0 6px',
        },
    };
    return menuItem;
}

// logic used in getShareableCalendarMenuSections in owa-calendar-ribbon.
// If you change one, change the other.
export function getShareableCalendarMenuItems(menuItemClassName?: string): IContextualMenuItem[] {
    const sharedCalendarEntries: Calendar[] = getShareableCalendars();
    if (sharedCalendarEntries.length <= 1) {
        return null;
    }

    const connectedAccounts = getOWAConnectedAccounts();

    if (connectedAccounts.length == 0) {
        return sharedCalendarEntries.map(cal => {
            return getBulletedCalendarMenuItem(cal, menuItemClassName);
        });
    } else {
        const accountsList = [
            getUserConfiguration().SessionSettings.UserEmailAddress,
            ...connectedAccounts.map(account => {
                return account.userIdentity;
            }),
        ];
        const sharedCalendarEntriesPerMailbox = accountsList.map(emailAddress => {
            return getSharedCalendarEntriesForEmailAddress(sharedCalendarEntries, emailAddress);
        });

        return getCombinedCalendarMenuItems(
            sharedCalendarEntriesPerMailbox.map(calEntries => {
                const bulletItems = calEntries.map(cal => {
                    return getBulletedCalendarMenuItem(cal, menuItemClassName);
                });

                const sharedCalendarMenuItemsMap = new ObservableMap<
                    string,
                    FilterableMenuItem[]
                >();

                // There are instances, wherein the account gets loaded first and the calendars
                // take a while to load, and the list of shared calendar entries can be empty
                if (calEntries[0]) {
                    return sharedCalendarMenuItemsMap.set(
                        calEntries[0].id.mailboxInfo.userIdentity,
                        bulletItems
                    );
                } else {
                    return undefined;
                }
            })
        );
    }
}

function getSharedCalendarEntriesForEmailAddress(
    sharedCalendars: Calendar[],
    emailAddress: string
) {
    return sharedCalendars.filter(cal => {
        return cal.id.mailboxInfo.userIdentity === emailAddress;
    });
}
